import type {
  ApiResponse,
  Album,
  AlbumList,
  ArchiveMonth,
  CaptchaData,
  Category,
  Comment,
  CommentSubmitSetting,
  CommentAnchorResult,
  CommentCreateInput,
  CommentCreateResult,
  CommentLoginCreateInput,
  Content,
  ContentDetail,
  ContentMeta,
  ContentListQuery,
  CustomFieldDef,
  FriendLinkListQuery,
  FriendLinkListResult,
  FriendLinkRSSListQuery,
  FriendLinkRSSItem,
  FriendLinkStatus,
  GlobalSetting,
  HotContentListQuery,
  LatestComment,
  LinkApplyInput,
  LikeActionResult,
  PluginEngineInfoResponse,
  PluginLoginExchange,
  PageResult,
  RandomContent,
  RandomFriendLink,
  SiteStats,
  Tag,
  VerifyTokenResponse
} from '~/types/blog'
import {
  clearPluginAuthSession,
  getPluginAuthToken,
  getPluginAuthUserID,
  refreshPluginAuthSession
} from '~/utils/pluginAuth'

function toQuery(params: Record<string, unknown>) {
  return Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== '')
  )
}

function resolveHttpStatusFromCode(code: number) {
  if (code >= 1000 && code < 1100) return 400
  if (code >= 1100 && code < 1200) return 401
  if (code >= 1200 && code < 1300) return 403
  if (code >= 1300 && code < 1400) return 404
  if (code >= 1400 && code < 1500) return 409
  if (code >= 1500 && code < 1600) return 429
  if (code >= 1600 && code < 1700) return 502
  if (code >= 1700 && code < 1800) return 500
  if (code >= 1800 && code < 1900) return 504
  return 500
}

// isAuthBusinessCode 判断业务码是否表示登录认证失败。
function isAuthBusinessCode(code: number) {
  return code >= 1100 && code < 1200
}

type RequestOptions<T> = Parameters<typeof $fetch<ApiResponse<T>>>[1] & { throwError?: boolean }

function sanitizePageResult<T>(payload: PageResult<T> | null | undefined): PageResult<T> {
  const list = Array.isArray(payload?.list) ? payload.list.filter(item => item !== null && item !== undefined) as T[] : []
  return {
    ...(payload || {}),
    list,
    total: Number(payload?.total || 0)
  } as PageResult<T>
}

export const useBlogApi = () => {
  const config = useRuntimeConfig()
  const apiBase = config.public.apiBase

  async function request<T>(
    path: string,
    options: RequestOptions<T> = {}
  ) {
    const { throwError = true, ...fetchOptions } = options

    // 为所有请求复用插件互联会话，受保护接口由后端 JWT 与 Casbin 统一鉴权。
    const headers = new Headers(fetchOptions.headers as HeadersInit | undefined)
    const token = getPluginAuthToken()
    const userID = getPluginAuthUserID()
    if (token && !headers.has('x-token')) {
      headers.set('x-token', token)
    }
    if (userID && !headers.has('x-user-id')) {
      headers.set('x-user-id', userID)
    }

    try {
      // 读取完整响应以接收 JWT 中间件返回的无感续期响应头。
      const rawResponse = await $fetch.raw<ApiResponse<T>>(`${apiBase}${path}`, {
        ...fetchOptions,
        headers,
        retry: 0
      })
      const newToken = String(rawResponse.headers.get('new-token') || '').trim()
      const newExpiresAt = String(rawResponse.headers.get('new-expires-at') || '').trim()
      if (newToken && newExpiresAt) {
        refreshPluginAuthSession(newToken, newExpiresAt)
      }
      const response = rawResponse._data as ApiResponse<T>

      if (response.code !== 0 && throwError) {
        // 认证类业务错误统一清理会话，并由登录状态事件同步所有组件。
        if (isAuthBusinessCode(Number(response.code))) {
          clearPluginAuthSession()
        }
        const statusCode = resolveHttpStatusFromCode(Number(response.code))
        throw createError({
          statusCode,
          message: response.msg || '接口请求失败',
          data: {
            businessCode: response.code,
            payload: response.data
          }
        })
      }
      return response.data
    } catch (error: any) {
      const backendCode = Number(error?.data?.code || error?.response?._data?.code || 0)
      if (isAuthBusinessCode(backendCode)) {
        clearPluginAuthSession()
      }
      if (throwError && backendCode > 0) {
        const statusCode = Number(error?.statusCode || resolveHttpStatusFromCode(backendCode))
        throw createError({
          statusCode,
          message: error?.data?.msg || error?.response?._data?.msg || '接口请求失败',
          data: {
            businessCode: backendCode,
            payload: error?.data?.data ?? error?.response?._data?.data
          }
        })
      }

      if (throwError) {
        throw error
      }
      // 静默模式返回 null 让业务逻辑自己决定
      return null as unknown as T
    }
  }

  return {
    getPluginEngineInfo: (redirect?: string) =>
      request<PluginEngineInfoResponse>('/plugin/engine/info', { query: toQuery({ redirect }) }),
    exchangePluginEngineLogin: (code: string) =>
      request<PluginLoginExchange>('/plugin/engine/exchange', { method: 'POST', body: { code } }),
    verifyPluginEngineToken: () => request<VerifyTokenResponse>('/jwt/verifyToken'),
    logoutPluginEngine: () => request<null>('/jwt/logout', { method: 'POST' }),

    getGlobalSetting: () => request<GlobalSetting>('/blog/setting/getGlobalSetting'),
    getFieldDefList: (scope?: string) => request<CustomFieldDef[]>('/blog/field/getFieldDefList', { query: toQuery({ scope }) }),

    getContentList: async (query: ContentListQuery = {}, options: RequestOptions<PageResult<Content>> = {}) => {
      const payload = await request<PageResult<Content>>('/blog/content/getContentList', {
        ...options,
        query: toQuery(query as Record<string, unknown>)
      })
      return sanitizePageResult(payload)
    },
    getContentMetaList: async (query: ContentListQuery = {}, options: RequestOptions<PageResult<ContentMeta>> = {}) => {
      const payload = await request<PageResult<ContentMeta>>('/blog/content/getContentMetaList', {
        ...options,
        query: toQuery(query as Record<string, unknown>)
      })
      return sanitizePageResult(payload)
    },

    getContent: (id: number, options: RequestOptions<ContentDetail> = {}) =>
      request<ContentDetail>('/blog/content/getContent', { ...options, query: { id } }),
    getContentMeta: (id: number, options: RequestOptions<ContentMeta> = {}) =>
      request<ContentMeta>('/blog/content/getContentMeta', { ...options, query: { id } }),
    getContentBySlug: (slug: string, options: RequestOptions<ContentDetail> = {}) =>
      request<ContentDetail>('/blog/content/getContentBySlug', { ...options, query: { slug } }),
    getContentMetaBySlug: (slug: string, options: RequestOptions<ContentMeta> = {}) =>
      request<ContentMeta>('/blog/content/getContentMetaBySlug', { ...options, query: { slug } }),
    getRandomContent: () => request<RandomContent>('/blog/content/getRandomContent'),
    getHotContentList: (query: HotContentListQuery = {}, options: RequestOptions<ContentMeta[]> = {}) =>
      request<ContentMeta[]>('/blog/content/getHotContentList', {
        ...options,
        query: toQuery(query as Record<string, unknown>)
      }),
    likeContent: (id: number) => request<LikeActionResult>('/blog/content/likeContent', { method: 'POST', body: { id } }),

    getCategoryTree: (options: RequestOptions<Category[]> = {}) => request<Category[]>('/blog/category/getCategoryTree', options),
    getTagCloud: () => request<Tag[]>('/blog/category/getTagCloud'),
    getArchives: (options: RequestOptions<ArchiveMonth[]> = {}) =>
      request<ArchiveMonth[]>('/blog/category/getArchives', options),
    getSiteStats: () => request<SiteStats>('/blog/category/getSiteStats'),

    getAlbumList: (query: ContentListQuery = {}) =>
      request<PageResult<AlbumList>>('/blog/album/getAlbumList', { query: toQuery(query as Record<string, unknown>) }),
    getAlbumBySlug: (slug: string, options: RequestOptions<Album> = {}) =>
      request<Album>('/blog/album/getAlbumBySlug', { ...options, query: { slug } }),
    likeAlbum: (id: number) => request<LikeActionResult>('/blog/album/likeAlbum', { method: 'POST', body: { id } }),

    getFriendLinkList: (query: FriendLinkListQuery = {}) =>
      request<FriendLinkListResult>('/blog/link/getFriendLinkList', { query: toQuery(query as Record<string, unknown>) }),
    getFriendLinkRSSList: async (query: FriendLinkRSSListQuery = {}, options: RequestOptions<PageResult<FriendLinkRSSItem>> = {}) => {
      const payload = await request<PageResult<FriendLinkRSSItem>>('/blog/link/getFriendLinkRSSList', {
        ...options,
        query: toQuery(query as Record<string, unknown>)
      })
      return sanitizePageResult(payload)
    },
    getFriendLinkStatus: (query: { email?: string; url?: string; page?: number; pageSize?: number }) =>
      request<PageResult<FriendLinkStatus>>('/blog/link/getFriendLinkStatus', { query: toQuery(query as Record<string, unknown>) }),
    getRandomFriendLink: () => request<RandomFriendLink>('/blog/link/getRandomFriendLink'),
    applyFriendLink: (payload: LinkApplyInput) => request<null>('/blog/link/applyFriendLink', { method: 'POST', body: payload }),

    getCommentTree: (contentId: number, query?: { page?: number; pageSize?: number }) =>
      request<PageResult<Comment>>('/blog/comment/getCommentTree', { query: toQuery({ contentId, ...(query || {}) }) }),
    getCommentAnchor: (contentId: number, commentId: number, pageSize?: number) =>
      request<CommentAnchorResult>('/blog/comment/getCommentAnchor', { query: toQuery({ contentId, commentId, pageSize }) }),
    getLatestComments: (limit = 8) => request<LatestComment[]>('/blog/comment/getLatestComments', { query: { limit } }),
    createComment: (payload: CommentCreateInput) => request<null>('/blog/comment/createComment', { method: 'POST', body: payload }),
    createLoginComment: (payload: CommentLoginCreateInput) =>
      request<CommentCreateResult>('/blog/comment/createLoginComment', { method: 'POST', body: payload }),
    getCommentSubmitSetting: () => request<CommentSubmitSetting>('/blog/comment/getCommentSubmitSetting'),

    getCaptcha: () => request<CaptchaData>('/base/captcha', { method: 'POST' })
  }
}
